/**
 * StreamingStatusBar — real-time SSE progress indicator.
 *
 * A slim animated bar rendered above the HubList body whenever the
 * inventory stream is live. Every animation in this component is
 * driven by a real SSE event — there are no fake timers, no fake
 * progress increments.
 *
 * Layout (28px tall):
 *   [● dot] [phase label · doc name]   [track ══════░░░]  [N/M]
 *
 * Progress track behaviour:
 *   checking_cache / cache_miss  → indeterminate shimmer (no fake %,
 *                                  just signals "working")
 *   classifying with index/total → deterministic fill, advances
 *                                  400ms ease-out per event
 *   cache_hit                    → instant 100% fill, then dim
 *   done                         → fills to 100%, collapses after
 *                                  600ms CSS transition
 *   error                        → red track, stays visible
 *
 * The component NEVER renders when the stream is idle.
 *
 * Injected CSS keyframe: "ssb-shimmer" (injected once, guarded).
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useState } from "react";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../../tokens";
import type { InventoryStreamState, StreamPhase } from "../hooks/useInventoryStream";
import { aiColors } from "../tokens";

export interface StreamingStatusBarProps {
  stream: InventoryStreamState;
}

// ─── One-time keyframe injection ───────────────────────────────────

const KF_ID = "ssb-shimmer-keyframes";
if (typeof document !== "undefined" && !document.getElementById(KF_ID)) {
  const el = document.createElement("style");
  el.id = KF_ID;
  el.textContent = `
    @keyframes ssb-shimmer {
      0%   { background-position: -200% center; }
      100% { background-position:  200% center; }
    }
    @keyframes ssb-collapse {
      from { max-height: 42px; opacity: 1; }
      to   { max-height: 0;    opacity: 0; }
    }
    @keyframes ssb-dot-breathe {
      0%, 100% { opacity: 1;   transform: scale(1);    }
      50%       { opacity: 0.3; transform: scale(0.7);  }
    }
  `;
  document.head.appendChild(el);
}

// ─── Phase → display config ────────────────────────────────────────

interface PhaseDisplay {
  dotColor: string;
  trackColor: string;
  trackDimColor: string;
  pulseDot: boolean;
  label: string;
}

function phaseDisplay(phase: StreamPhase, stream: InventoryStreamState): PhaseDisplay {
  switch (phase) {
    case "checking":
      return {
        dotColor: aiColors.thinking,
        trackColor: aiColors.thinking,
        trackDimColor: "var(--brand-soft)",
        pulseDot: true,
        label: "Checking library…",
      };
    case "classifying": {
      const docLabel = stream.currentDocName ? `Understanding ${stream.currentDocName}` : "Understanding documents…";
      return {
        dotColor: aiColors.classifying,
        trackColor: aiColors.classifying,
        trackDimColor: "var(--brand-soft)",
        pulseDot: true,
        label: docLabel,
      };
    }
    case "done":
      return {
        dotColor: aiColors.evidenceHigh,
        trackColor: aiColors.evidenceHigh,
        trackDimColor: "var(--success-soft)",
        pulseDot: false,
        label: "Library intelligence ready",
      };
    case "error":
      return {
        dotColor: statusColors.danger,
        trackColor: statusColors.danger,
        trackDimColor: "var(--danger-soft)",
        pulseDot: false,
        label: "Stream error",
      };
    default:
      return {
        dotColor: brandColors.primary,
        trackColor: brandColors.primary,
        trackDimColor: surfaceColors.line,
        pulseDot: false,
        label: "Loading…",
      };
  }
}

// ─── Styles ────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    padding: "7px 14px",
    borderBottom: `1px solid ${aiColors.annotationBorder}`,
    backgroundColor: aiColors.annotationBg,
    flexShrink: 0,
    overflow: "hidden",
  },
  topRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "6px",
    minWidth: 0,
  },
  dot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  dotPulsing: {
    animationName: "ssb-dot-breathe",
    animationDuration: "1.6s",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
  },
  label: {
    flex: 1,
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  counter: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    flexShrink: 0,
    letterSpacing: "0.3px",
  },
  trackWrap: {
    width: "100%",
    height: "2px",
    borderRadius: "9999px",
    overflow: "hidden",
  },
  trackFill: {
    height: "100%",
    borderRadius: "9999px",
    transitionProperty: "width",
    transitionDuration: "400ms",
    transitionTimingFunction: "ease-out",
  },
  trackFillShimmer: {
    // Shimmer for indeterminate phases — not fake progress, just a
    // visual "alive" signal while we have no position data.
    width: "100%",
    backgroundSize: "200% 100%",
    animationName: "ssb-shimmer",
    animationDuration: "2.4s",
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
    height: "100%",
  },
});

// ─── Component ─────────────────────────────────────────────────────

/** Auto-hide timing: bar stays 600ms after `done` before unmounting. */
const DONE_HOLD_MS = 600;

export const StreamingStatusBar: React.FC<StreamingStatusBarProps> = ({ stream }) => {
  const styles = useStyles();
  // Hold the bar visible for DONE_HOLD_MS after stream completes.
  const [visible, setVisible] = useState(false);
  const [doneHeld, setDoneHeld] = useState(false);

  useEffect(() => {
    if (stream.isLive || stream.phase === "checking" || stream.phase === "classifying") {
      setVisible(true);
      setDoneHeld(false);
    } else if (stream.phase === "done") {
      setVisible(true);
      setDoneHeld(true);
      const t = setTimeout(() => setVisible(false), DONE_HOLD_MS);
      return () => clearTimeout(t);
    } else if (stream.phase === "error") {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [stream.isLive, stream.phase]);

  if (!visible) return null;

  const display = phaseDisplay(stream.phase, stream);
  const pct = Math.round(stream.progress * 100);

  // Shimmer: use when indeterminate AND not done
  const useShimmer = stream.isIndeterminate && stream.phase !== "done" && stream.phase !== "error";

  return (
    <output
      className={styles.root}
      aria-live="polite"
      aria-label={`Library processing: ${display.label}`}
      style={{
        animationName: doneHeld ? "ssb-collapse" : "none",
        animationDuration: `${DONE_HOLD_MS}ms`,
        animationTimingFunction: "ease-out",
        animationFillMode: "forwards",
        animationDelay: "0ms",
      }}
      data-testid="streaming-status-bar"
    >
      <div className={styles.topRow}>
        <span
          className={`${styles.dot} ${display.pulseDot ? styles.dotPulsing : ""}`}
          style={{ backgroundColor: display.dotColor }}
          aria-hidden="true"
        />
        <span className={styles.label} style={{ color: display.dotColor }}>
          {display.label}
        </span>
        {stream.currentDocIndex !== null && stream.totalDocs !== null && (
          <span className={styles.counter}>
            {stream.currentDocIndex}&thinsp;/&thinsp;{stream.totalDocs}
          </span>
        )}
        {stream.phase === "done" && (
          <span className={styles.counter} style={{ color: aiColors.evidenceHigh }}>
            ✓ done
          </span>
        )}
      </div>

      {/* ── Progress track ── */}
      <div className={styles.trackWrap} style={{ backgroundColor: display.trackDimColor }}>
        {useShimmer ? (
          <div
            className={styles.trackFillShimmer}
            style={{
              backgroundImage: `linear-gradient(
                90deg,
                ${display.trackDimColor} 0%,
                ${display.trackColor} 45%,
                ${display.trackColor} 55%,
                ${display.trackDimColor} 100%
              )`,
            }}
            aria-hidden="true"
          />
        ) : (
          <div
            className={styles.trackFill}
            style={{
              width: `${pct}%`,
              backgroundColor: display.trackColor,
            }}
            aria-hidden="true"
          />
        )}
      </div>
    </output>
  );
};

export default StreamingStatusBar;
